<template>
  <div>
    <BlBackdrop
      title="Edit availability"
      :label="submitLoading ? 'Updating' : isRecurring ? 'Next' : 'Update'"
      :disabled="isDisabledSubmit"
      :submitLoading="submitLoading"
      @backdrop-close="$emit('close-modal')"
      @backdrop-action="onNextToRecurring"
    >
      <div
        :class="{
          'overflow-y-auto': conflictedByRoom.length || conflictedByUser.length
        }"
        :style="{ width: '40rem', maxHeight: 'calc(100vh - 14rem)' }"
      >
        <div v-if="modalLoading" class="grid place-items-center h-100">
          <div class="blum-loader"></div>
        </div>
        <div v-else-if="availability" class="flex flex-col gap-4 px-8">
          <div class="flex">
            <div class="flex items-center gap-1 px-1 rounded h-7 bg-tulip-100">
              <BlIcon icon="calendar-blank" color="tulip-7" class="w-6 h-6" />
              <BlTextSubtitle3
                :text="
                  displayDate({
                    date: availabilityDate,
                    format: 'ddd, DD MMMM YYYY'
                  })
                "
                class="text-tulip-700"
              />
            </div>
          </div>
          <div v-if="availability.repetition_type !== 'not_repeat'">
            <BlTextBody2
              text="This availability is part of a recurring schedule that repeats every"
            />
            <BlTextBody2
              :text="
                `${availability.interval} ${availability.repetition_type}(s)`
              "
              class="font-bold"
            />
            <BlTextBody2 text="on" />
            <BlTextBody2 :text="repetitionText" class="font-bold" />
            <BlTextBody2 text="and" />
            <BlTextBody2
              v-if="availability.is_never_end"
              text="continues indefinitely."
              class="font-bold"
            />
            <BlTextBody2
              v-else
              :text="
                `ends on ${displayDate({
                  date: availability.end_date || availability.end_after,
                  fornat: 'DD MMMM YYYY'
                })}.`
              "
              class="font-bold"
            />
          </div>
          <div class="flex items-center gap-2">
            <BlIcon icon="user" color="slate-6" class="w-6 h-6" />
            <div
              v-if="kindInput === 'for_therapist'"
              class="flex items-center gap-2"
            >
              <BlSelectBox
                class="w-96"
                search
                v-model="therapistInput"
                placeholder="Select therapist"
                :options="defaultTherapistOptions"
                :fetchProperty="{
                  url: `/api/v1/users?sort=name_asc&status=active&query=`,
                  dataKey: 'users',
                  countKey: 'total_users',
                  optionKeys: { value: 'id', label: 'name' }
                }"
                :disabled="availability.user_id !== null"
                @change="therapistInput = $event"
              />

              <BlTooltip>
                <template v-if="isDisabledSetAsOpenRoom" v-slot:tooltip>
                  <div class="flex flex-col w-40 gap-1 text-center">
                    This availability already has ongoing and/or completed
                    appointments, so it cannot be open room availability.
                  </div>
                </template>
                <BlInputButton
                  type="plain"
                  label="Set as open room"
                  :disabled="isDisabledSetAsOpenRoom"
                  @clicked="kindInput = 'open_room'"
                />
              </BlTooltip>
            </div>
            <div
              v-if="kindInput === 'open_room'"
              class="flex items-center gap-2"
            >
              <BlSelectBox
                key="open-room-placeholder"
                class="w-112"
                v-model="openRoomInput"
                :options="[
                  { value: 0, label: 'Open room - available to everyone' }
                ]"
                disabled
              />

              <BlTooltip>
                <template v-if="isDisabledRemoveOpenRoom" v-slot:tooltip>
                  <div class="flex flex-col w-40 gap-1 text-center">
                    This availability already has appointments, so it cannot be
                    reassigned to a specific therapist.
                  </div>
                </template>
                <BlInputButton
                  type="plain"
                  label="Remove"
                  :disabled="isDisabledRemoveOpenRoom"
                  @clicked="kindInput = 'for_therapist'"
                />
              </BlTooltip>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <BlIcon icon="clock-outline" color="slate-6" class="w-6 h-6" />
            <BlSelectBox
              class="w-64"
              v-model="startTimeInput"
              search
              placeholder="From"
              :options="startTimeOptions"
              @change="startTimeInput = $event"
            />
            <BlIcon icon="arrow-right" color="slate-6" class="w-6 h-6" />
            <BlSelectBox
              :key="`end-time-${startTimeInput}`"
              class="w-62"
              v-model="endTimeInput"
              search
              placeholder="To"
              :options="endTimeOptions"
              @change="endTimeInput = $event"
            />
          </div>
          <div v-if="enableBranch" class="flex items-center gap-2">
            <BlIcon icon="location" color="slate-6" class="w-6 h-6" />
            <BlSelectBox
              class="w-136"
              type="branch"
              search
              placeholder="Select branch"
              v-model="branchInput"
              :options="defaultBranchOptions"
              :fetchProperty="{
                url: `/api/v1/branches?sort=name_asc&query=`,
                dataKey: 'branches',
                countKey: 'total_count',
                optionKeys: {
                  value: 'id',
                  label: 'name',
                  color: 'color',
                  backgroundColor: 'background_color'
                }
              }"
              :disabled="!endTimeInput"
              @change="branchInput = $event"
              drop="up"
            />
          </div>
          <div class="flex items-center gap-2">
            <BlIcon icon="door" color="slate-6" class="w-6 h-6" />
            <BlSelectBox
              :key="`room-${branchInput}`"
              class="w-136"
              search
              v-model="roomInput"
              :placeholder="roomSelectLoading ? 'Loading...' : 'Select room'"
              :options="roomOptions"
              :disabled="!endTimeInput"
              @change="roomInput = $event"
              drop="up"
            />
          </div>

          <div
            v-if="isShowInfoOrphanAppointments && !isRecurring"
            class="flex items-start gap-2 px-3 py-2 rounded bg-cornflower-200"
          >
            <BlIcon icon="info" color="cornflower-8" class="w-6 h-6" />
            <div>
              <BlTextBody2
                text="By switching the availability from"
                class="text-cornflower-800"
              />
              <BlTextBody2
                :text="availability.user.name"
                class="font-bold text-cornflower-800"
              />
              <BlTextBody2 text="to" class="text-cornflower-800" />
              <BlTextBody2
                text="'Open room',"
                class="font-bold text-cornflower-800"
              />
              <BlTextBody2 text="all" class="text-cornflower-800" />
              <BlTextBody2
                :text="`${availability.appointments.length} appointment(s)`"
                class="font-bold text-cornflower-800"
              />
              <BlTextBody2 text="on" class="text-cornflower-800" />
              <BlTextBody2
                :text="
                  `${displayDate({
                    date: availabilityDate,
                    format: 'DD MMMM YYYY'
                  })},`
                "
                class="font-bold text-cornflower-800"
              />
              <BlTextBody2
                text="will no longer have an assigned therapist and will need to be reassigned."
                class="text-cornflower-800"
              />
            </div>
          </div>
          <div
            v-if="conflictedByRoom.length || conflictedByUser.length"
            class="flex items-start gap-2 px-3 py-2 rounded bg-tulip-100"
          >
            <BlIcon icon="warning" color="tulip-7" class="w-6 h-6" />
            <div class="flex flex-col w-128">
              <div v-if="conflictedByRoom.length" class="flex flex-col">
                <div>
                  <BlTextBody2 :text="suffixRoomError" class="text-tulip-700" />
                </div>
                <div
                  v-for="(date, idx) in conflictedByRoom"
                  :key="idx"
                  class="flex items-center gap-2 px-2"
                >
                  <div class="w-1 h-1 rounded bg-tulip-700"></div>
                  <BlTextBody2 :text="date" class="text-tulip-700" />
                </div>
              </div>
              <div v-if="conflictedByUser.length" class="flex flex-col">
                <div>
                  <BlTextBody2
                    v-if="conflictedByRoom.length"
                    text="Additionally,"
                    class="text-tulip-700"
                  />
                  <BlTextBody2 :text="suffixUserError" class="text-tulip-700" />
                </div>
                <div
                  v-for="(date, idx) in conflictedByUser"
                  :key="idx"
                  class="flex items-center gap-2 px-2"
                >
                  <div class="w-1 h-1 rounded bg-tulip-700"></div>
                  <BlTextBody2 :text="date" class="text-tulip-700" />
                </div>
              </div>
              <BlTextBody2
                text="Please adjust the details to proceed with the availability."
                class="text-tulip-700"
              />
            </div>
          </div>
        </div>
        <div v-if="availability" class="absolute z-10 left-8 -bottom-14">
          <BlTooltip>
            <template v-if="isDisabledDelete" v-slot:tooltip>
              <div class="flex flex-col w-40 gap-1 text-center">
                This availability has in-progress and/or completed appointments
                and cannot be removed.
              </div>
            </template>
            <BlInputButton
              type="plain-error"
              icon="delete"
              label="Delete availability"
              :disabled="isDisabledDelete"
              @clicked="deleteOpened = true"
            />
          </BlTooltip>
        </div>
        <div v-if="submitLoading" class="absolute z-10 right-56 -bottom-12">
          <div class="blum-loader-btn"></div>
        </div>
      </div>
    </BlBackdrop>

    <BlBackdrop
      v-if="recurringOpened"
      title="Edit recurring availabilities"
      label="Update"
      description="You are modifying a recurring availability slot. Do you want to update only this slot or all future slots in the series?"
      @backdrop-close="onBackToEdit"
      @backdrop-action="onSubmit"
    >
      <div class="flex flex-col gap-4 px-8" :style="{ width: '40rem' }">
        <div class="grid items-center grid-cols-2 gap-2">
          <div
            v-for="opt in updateOptionOptions"
            :key="opt.value"
            class="flex flex-col items-center justify-center h-full px-4 py-4 text-center rounded cursor-pointer w-70"
            :class="[
              opt.value === updateOptionInput
                ? 'border-2 border-light-purple bg-slate-100'
                : 'border border-slate-400 bg-pure-white m-px'
            ]"
            @click="updateOptionInput = opt.value"
          >
            <BlTextSubtitle3
              :text="opt.title"
              :class="{ 'text-light-purple': opt.value === updateOptionInput }"
            />
          </div>
        </div>

        <div
          v-if="isShowInfoOrphanAppointments"
          class="flex items-start gap-2 px-3 py-2 rounded bg-cornflower-200"
        >
          <BlIcon icon="info" color="cornflower-8" class="w-6 h-6" />
          <div>
            <BlTextBody2
              text="By switching the availability from"
              class="text-cornflower-800"
            />
            <BlTextBody2
              :text="availability.user.name"
              class="font-bold text-cornflower-800"
            />
            <BlTextBody2 text="to" class="text-cornflower-800" />
            <BlTextBody2
              text="'Open room',"
              class="font-bold text-cornflower-800"
            />
            <BlTextBody2 text="all" class="text-cornflower-800" />
            <BlTextBody2
              :text="`${impactedAppointment.count} appointment(s)`"
              class="font-bold text-cornflower-800"
            />
            <BlTextBody2
              v-if="updateOptionInput === 'current'"
              text="on"
              class="text-cornflower-800"
            />
            <BlTextBody2
              v-if="updateOptionInput === 'current'"
              :text="
                `${displayDate({
                  date: availabilityDate,
                  format: 'DD MMMM YYYY'
                })},`
              "
              class="font-bold text-cornflower-800"
            />
            <BlTextBody2
              v-if="updateOptionInput === 'current_and_following'"
              text="from"
              class="text-cornflower-800"
            />
            <BlTextBody2
              v-if="updateOptionInput === 'current_and_following'"
              :text="
                displayDate({ date: availabilityDate, format: 'DD MMMM YYYY' })
              "
              class="font-bold text-cornflower-800"
            />
            <BlTextBody2
              v-if="updateOptionInput === 'current_and_following'"
              text="to"
              class="text-cornflower-800"
            />
            <BlTextBody2
              v-if="updateOptionInput === 'current_and_following'"
              :text="
                `${displayDate({
                  date: impactedAppointment.last_date,
                  format: 'DD MMMM YYYY'
                })},`
              "
              class="font-bold text-cornflower-800"
            />
            <BlTextBody2
              text="will no longer have an assigned therapist and will need to be reassigned."
              class="text-cornflower-800"
            />
          </div>
        </div>
      </div>
    </BlBackdrop>

    <BlBackdrop
      v-if="deleteOpened"
      title="Delete availability"
      label="Delete"
      type="error"
      :disabled="conflictedAppointments.length"
      :submitLoading="deleteLoading"
      @backdrop-close="deleteOpened = false"
      @backdrop-action="onDelete"
    >
      <div v-if="!isRecurring" class="px-8">
        <BlTextBody2
          text="You are about to remove the availability slot from the schedule. If you proceed with deletion, the availability slot can be used by another therapist. Are you sure you want to proceed?"
        />
      </div>
      <div v-else class="flex flex-col gap-4 px-8">
        <div>
          <BlTextBody2
            text="This availability has been set as recurring slots until"
          />
          <BlTextBody2 :text="endDatePreview" class="font-bold" />
          <br />
          <BlTextBody2 text="What action would you like to take?" />
        </div>
        <div class="grid items-center grid-cols-2 gap-2">
          <div
            v-for="opt in deleteOptionOptions"
            :key="opt.value"
            class="flex flex-col items-center justify-center h-full px-4 py-4 text-center rounded cursor-pointer w-70"
            :class="[
              opt.value === deleteOptionInput
                ? 'border-2 border-light-purple bg-slate-100'
                : 'border border-slate-400 bg-pure-white m-px'
            ]"
            @click="deleteOptionInput = opt.value"
          >
            <BlTextSubtitle3
              :text="opt.title"
              :class="{ 'text-light-purple': opt.value === deleteOptionInput }"
            />
          </div>
        </div>
        <div v-if="conflictedAppointments.length" class="flex flex-col gap-2">
          <div>
            <BlTextBody2 text="This action will affect" />
            <BlTextBody2
              :text="totalConflictedAppointments"
              class="font-bold"
            />
            <BlTextBody2 text="scheduled appointment(s)." />
            <BlTextBody2
              text="Please resolve the appointment(s) before proceeding with the deletion."
            />
          </div>

          <div
            class="flex flex-col gap-1 px-4 py-3 overflow-y-auto rounded max-h-64 bg-slate-200"
          >
            <div
              v-for="item in conflictedAppointments"
              :key="item.date"
              class="flex items-center justify-between"
            >
              <div class="flex items-center gap-2">
                <BlTextBody2
                  :text="`${displayDate({ date: item.date })}:`"
                  class="text-slate-600"
                />
                <BlTextBody2
                  :text="`${item.count} appointment(s)`"
                  class="text-slate-800"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </BlBackdrop>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { displayDate } from "@/plugins/helper.js";
import moment from "moment";

import BlBackdrop from "../../BlBackdrop.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody3 from "../../BlTextBody3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";
import BlTooltip from "../../BlTooltip.vue";
import BlInputText from "../../BlInputText.vue";
import BlInputDate from "../../BlInputDate.vue";
import BlInputRadio from "../../BlInputRadio.vue";
import BlSelectBox from "../../BlSelectBox.vue";
import BlInputButton from "../../BlInputButton.vue";

export default {
  name: "EditAvailabilitiesModal",
  components: {
    BlBackdrop,
    BlTextSubtitle3,
    BlTextBody3,
    BlTextBody2,
    BlIcon,
    BlTooltip,
    BlInputText,
    BlInputDate,
    BlInputRadio,
    BlSelectBox,
    BlInputButton
  },
  props: {
    availabilityId: { type: Number, required: true },
    availabilityDate: { type: String, required: true }
  },
  data() {
    return {
      modalLoading: true,
      submitLoading: false,
      //
      availability: null,
      kindInput: "for_therapist", // for_therapist | open_room
      therapistInput: 0,
      openRoomInput: 0,
      startTimeInput: "",
      endTimeInput: "",
      //
      branchInput: 0,
      roomInput: 0,
      rooms: [],
      roomSelectLoading: false,
      repeatInput: "not_repeat",
      //
      repeatDetailsOpened: false,
      customized: false,
      //
      intervalInput: "1",
      repetitionTypeInput: "week", // day | week | month | year
      repeatEndInput: "never", // never | on_date | after_occurrences
      //
      daysOfWeekInput: [], // (monday | tuesday | wednesday | thursday | friday | saturday | sunday)[]
      dayOfMonthInput: null, // 1 - 31 (day)
      weekOfMonthInput: null, // first, second, third, fourth, last (week)
      //
      endAfterInput: "",
      occurrencesInput: "",
      conflictedByRoom: [],
      conflictedByUser: [],
      //
      recurringOpened: false,
      updateOptionInput: "current",
      updateOptionOptions: [
        {
          value: "current",
          title: "Apply to this availability only"
        },
        {
          value: "current_and_following",
          title: "Apply to this and all remaining availabilities"
        }
      ],
      //
      deleteOpened: false,
      deleteLoading: false,
      deleteOptionInput: "current",
      deleteOptionOptions: [
        {
          value: "current",
          title: "Delete only this availability"
        },
        {
          value: "current_and_following",
          title: "Delete this and all following availabilities"
        }
      ],
      //
      conflictedAppointments: []
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter", "user", "room"]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    slotType() {
      // room, therapist
      return this.$route.params.tab || "room";
    },
    repetitionText() {
      let text = "";
      if (!this.availability) return text;
      if (this.availability.repetition_type === "week") {
        text = this.availability.days_of_week
          .map(i => this.uppercaseFirstLetter(i))
          .join(", ");
      }
      if (this.availability.repetition_type === "month") {
        if (this.availability.day_of_month) {
          text = `day ${this.availability.day_of_month}`;
        }
        if (this.availability.week_of_month) {
          const ordinals = {
            first: "1st",
            second: "2nd",
            third: "3rd",
            fourth: "4th",
            last: "5th"
          };
          const ordinal = ordinals[this.availability.week_of_month];
          const dayOfWeek = moment(this.availability.start_date).format("dddd");
          text = `the ${ordinal} ${dayOfWeek}`;
        }
      }
      if (this.availability.repetition_type === "year") {
        text = moment(this.availability.start_date).format("MMMM DD");
      }
      return text;
    },
    isDisabledSetAsOpenRoom() {
      if (!this.availability) return true;
      return (
        this.availability.user_id !== null &&
        !this.availability.can_switch_therapist
      );
    },
    isDisabledRemoveOpenRoom() {
      if (!this.availability) return true;
      return (
        this.availability.user_id === null &&
        !this.availability.can_switch_therapist
      );
    },
    isShowInfoOrphanAppointments() {
      if (!this.availability) return false;
      const appointments = this.availability.appointments;
      return (
        this.availability.user_id &&
        this.kindInput === "open_room" &&
        appointments.length > 0
      );
    },
    //
    defaultTherapistOptions() {
      if (!this.availability) return [];
      if (!this.availability.user_id) return [];
      if (this.availability.user) {
        const therapist = this.availability.user;
        return [{ value: therapist.id, label: therapist.name }];
      }
      return [{ value: this.availability.user_id, label: "" }];
    },
    defaultBranchOptions() {
      if (!this.availability) return [];
      const arr = [
        { value: 0, label: "All branches", class: "-mx-2" },
        {
          value: "no",
          label: "No branch",
          color: "#667085",
          backgroundColor: "#F9FAFB"
        }
      ];

      const branch = this.availability.room && this.availability.room.branch;
      if (branch) {
        arr.unshift({
          value: branch.id,
          label: branch.name,
          color: branch.color,
          backgroundColor: branch.background_color
        });
      }
      return arr;
    },
    roomOptions() {
      if (!this.availability) return [];
      const roomId = this.availability.room_id;
      let rooms = [...this.rooms];
      if (this.enableBranch && this.branchInput) {
        rooms = rooms.filter(i => {
          if (this.branchInput === "no") return !i.branch_id;
          return i.branch_id === this.branchInput;
        });
      }

      const arr = rooms.map(i => {
        let branch = "";
        if (this.enableBranch && !this.branchInput && i.branch_id) {
          branch = `
            <div
              class="flex items-center justify-center px-2 -mx-4 truncate rounded max-w-32 h-7"
              style="background-color: ${i.branch.background_color || "unset"}"
            >
              <span
                class="text-sm truncate"
                style="color: ${i.branch.color || "unset"}"
              >
                ${i.branch.name}
              </span>
            </div>
          `;
        }
        const isBooked = roomId !== i.id && i.is_booked;
        return {
          value: i.id,
          label: i.name,
          disabled: isBooked,
          suffix: isBooked
            ? `<div class="flex items-center gap-8">${branch} Booked</div>`
            : branch
        };
      });

      const room = { value: this.availability.room_id, label: "" };
      if (this.availability.room) {
        room.label = this.availability.room.name;
      }
      if (this.enableBranch && this.branchInput) {
        if (this.availability.room && this.availability.room.branch) {
          if (this.availability.room.branch.id === this.branchInput) {
            arr.unshift(room);
          }
        }
      } else {
        arr.unshift(room);
      }

      return arr;
    },
    startTimeOptions() {
      const arr = Array.from({ length: 48 }, (v, i) => {
        const inMinutes = i * 30;
        const hours = String(Math.floor(inMinutes / 60)).padStart(2, "0");
        const minutes = inMinutes % 60 === 0 ? "00" : "30";
        const time = `${hours}:${minutes}`;
        return { value: time, label: time };
      });
      return arr;
    },
    endTimeOptions() {
      const arr = Array.from({ length: 48 }, (v, i) => {
        const inMinutes = (i + 1) * 30;
        const hours = String(Math.floor(inMinutes / 60)).padStart(2, "0");
        const minutes = inMinutes % 60 === 0 ? "00" : "30";
        const time = `${hours}:${minutes}`;
        return { value: time, label: time };
      });
      if (!this.startTimeInput) return arr;
      const start = this.startTimeInput.split(":").map(i => Number(i));
      return arr.filter(i => {
        const val = i.value.split(":").map(a => Number(a));
        if (val[0] > start[0]) return true;
        else if (val[0] === start[0] && val[1] > start[1]) return true;
        else return false;
      });
    },
    //
    isDisabledDelete() {
      if (!this.availability) return false;
      return !this.availability.is_deletable;
    },
    isDisabledSubmit() {
      if (!this.startTimeInput) return true;
      if (!this.endTimeInput) return true;
      if (!this.roomInput) return true;
      return false;
    },
    //
    suffixRoomError() {
      if (!this.availability) return "";
      const name =
        (this.room && this.room.name) ||
        (this.availability.room && this.availability.room.name) ||
        "The room";
      return `${name} isn't available for the selected timeslot (${this.startTimeInput} - ${this.endTimeInput}) on the following day(s):`;
    },
    suffixUserError() {
      if (!this.availability) return "";
      const name =
        (this.user && this.user.name) ||
        (this.availability.user && this.availability.user.name) ||
        "";
      return `${name} cannot use this room on the following day(s) due to existing availabilities and/or appointments:`;
    },
    //
    isRecurring() {
      if (!this.availability) return false;
      const repetitionType = this.availability.repetition_type;
      const timeslotDate = moment(this.availabilityDate).format("YYYY-MM-DD");
      const availabilityEndDate = moment(this.availability.end_date).format(
        "YYYY-MM-DD"
      );
      const isTrue =
        repetitionType !== null &&
        repetitionType !== "not_repeat" &&
        this.repetitionTypeInput !== "not_repeat";

      if (isTrue && timeslotDate === availabilityEndDate) return false;
      return isTrue;
    },
    impactedAppointment() {
      let count = 0;
      let lastDate = "";
      if (this.updateOptionInput === "current") {
        count = this.availability.appointments.filter(i => {
          return (
            moment(i.date).format("YYYY-MM-DD") ===
            moment(this.availabilityDate).format("YYYY-MM-DD")
          );
        }).length;
      }
      if (this.updateOptionInput === "current_and_following") {
        count = this.availability.appointments.length;
        const arr = [...this.availability.appointments].sort((a, b) => {
          return new Date(b.date) - new Date(a.date);
        });
        lastDate = arr[0].date;
      }
      return { count, last_date: lastDate };
    },
    endDatePreview() {
      if (!this.availability) return "";
      return moment(this.availability.end_date).format("DD MMM YYYY");
    },
    totalConflictedAppointments() {
      let n = 0;
      this.conflictedAppointments.forEach(i => {
        n += i.count;
      });
      return n;
    }
  },
  watch: {
    therapistInput: {
      immediate: true,
      handler(val) {
        this.getUser({ id: val });
      }
    },
    roomInput: {
      immediate: true,
      handler(val) {
        this.getRoom({ id: val });
      }
    },
    //
    startTimeInput() {
      this.endTimeInput = "";
    },
    endTimeInput(val) {
      if (val) this.fetchRooms();
    },
    branchInput(val) {
      if (this.enableBranch) {
        if (val) this.roomInput = 0;
      }
    },
    deleteOptionInput() {
      this.conflictedAppointments = [];
    }
  },
  async mounted() {
    this.modalLoading = true;

    const params = {
      id: this.availabilityId,
      params: `?date=${this.availabilityDate}`
    };
    const { data: availability } = await this.getAvailability(params);
    this.availability = availability;

    const userId = availability.user_id || 0;
    this.therapistInput = userId;
    if (!availability.user_id) {
      this.kindInput = "open_room";
    }

    this.startTimeInput = availability.start_time_string;

    if (availability.room && availability.room.branch) {
      this.branchInput = availability.room.branch.id;
    }
    setTimeout(() => {
      this.roomInput = availability.room_id;
    }, 10);

    setTimeout(() => {
      this.endTimeInput = availability.end_time_string;
    }, 20);

    const repetitionType = availability.repetition_type;
    if (repetitionType === null || repetitionType === "not_repeat") {
      this.repeatInput = "not_repeat";
    } else {
      this.customized = true;
      this.repeatInput = "customized";
      this.intervalInput = availability.interval;
      this.repetitionTypeInput = repetitionType;
      this.repeatEndInput = "never";
      this.daysOfWeekInput = availability.days_of_week;
      this.dayOfMonthInput = availability.day_of_month;
      this.weekOfMonthInput = availability.week_of_month;
      if (availability.end_after) this.repeatEndInput = "on_date";
      if (availability.occurrences) {
        this.repeatEndInput = "after_occurrences";
      }
      this.endAfterInput = availability.end_after;
      this.occurrencesInput = availability.occurrences;
    }

    this.modalLoading = false;
  },
  methods: {
    ...mapActions([
      "actionGet",
      "getAvailability",
      "getUser",
      "getRoom",
      "updateAvailabilities",
      "deleteAvailabilities"
    ]),
    displayDate,
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    uppercaseFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    async fetchRooms() {
      const params = {
        url: `/api/v1/rooms?date=${this.availabilityDate}&start_time=${this.startTimeInput}&end_time=${this.endTimeInput}`
      };

      this.roomSelectLoading = true;
      const { success, data } = await this.actionGet(params);
      this.roomSelectLoading = false;

      if (!success) return;
      this.rooms = data.rooms;
    },
    getSlotCount(start, end) {
      const s = start.split(":").map(i => Number(i));
      const e = end.split(":").map(i => Number(i));
      let slots = 0;
      slots += (e[0] - s[0]) * 2;
      if (s[1] === 30) slots -= 1;
      if (e[1] === 30) slots += 1;
      return slots;
    },
    onChangeRepeat(val) {
      this.repeatInput = val;
      if (val === "custom") this.repeatDetailsOpened = true;
    },
    onChangeRepeatDetails(val) {
      this.customized = true;
      this.repeatInput = "customized";

      this.intervalInput = val.intervalInput;
      this.repetitionTypeInput = val.repetitionTypeInput; // day | week | month | year
      this.repeatEndInput = val.repeatEndInput; // never | on_date | after_occurrences

      this.daysOfWeekInput = val.daysOfWeekInput; // (monday | tuesday | wednesday | thursday | friday | saturday | sunday)[]
      this.dayOfMonthInput = val.dayOfMonthInput; // 1 - 31 (day)
      this.weekOfMonthInput = val.weekOfMonthInput; // first, second, third, fourth, last (week)

      this.endAfterInput = val.endAfterInput;
      this.occurrencesInput = val.occurrencesInput;
    },
    onNextToRecurring() {
      if (this.isRecurring) {
        this.recurringOpened = true;
      } else {
        this.onSubmit();
      }
    },
    onBackToEdit() {
      this.recurringOpened = false;
      this.updateOptionInput = "current";
    },
    async onSubmit() {
      this.recurringOpened = false;

      const params = {
        id: this.availabilityId,
        room_name: this.room.name,
        availability: {
          user_id: this.kindInput === "open_room" ? null : this.therapistInput,
          room_id: this.roomInput,
          // start_date: this.availability.start_date, // updated date
          start_time: this.startTimeInput,
          end_time: this.endTimeInput,
          //
          // interval: null,
          // repetition_type: "not_repeat",
          // days_of_week: [],
          // day_of_month: null,
          // week_of_month: null,
          // end_after: null,
          // occurrences: null,
          //
          date: this.availabilityDate,
          update_option: this.updateOptionInput
        },
        message: null
      };

      // if (this.availability.user_id && this.repeatInput.includes("custom")) {
      //   params.availability.interval = this.intervalInput;
      //   params.availability.repetition_type = this.repetitionTypeInput;
      //   if (this.repetitionTypeInput === "week") {
      //     params.availability.days_of_week = this.daysOfWeekInput;
      //   }
      //   if (this.repetitionTypeInput === "month") {
      //     if (this.dayOfMonthInput) {
      //       params.availability.day_of_month = this.dayOfMonthInput;
      //     }
      //     if (this.weekOfMonthInput) {
      //       params.availability.week_of_month = this.weekOfMonthInput;
      //     }
      //   }
      //   if (this.repeatEndInput === "on_date") {
      //     params.availability.end_after = this.endAfterInput;
      //   }
      //   if (this.repeatEndInput === "after_occurrences") {
      //     params.availability.occurrences = this.occurrencesInput;
      //   }
      // }

      this.conflictedByRoom = [];
      this.conflictedByUser = [];
      this.submitLoading = true;
      const {
        success,
        conflicted_by_room: byRoom,
        conflicted_by_user: byUser
      } = await this.updateAvailabilities(params);
      if (!success) {
        this.submitLoading = false;
        const format = "DD MMM YYYY";
        this.conflictedByRoom = byRoom.map(date =>
          this.displayDate({ date, format })
        );
        this.conflictedByUser = byUser.map(date =>
          this.displayDate({ date, format })
        );
        return;
      }

      setTimeout(() => {
        this.submitLoading = false;
        this.$emit("after-commit");
        this.$emit("close-modal");
      }, 1000);
    },
    async onDelete() {
      const name = (this.user && this.user.name) || "Open room";
      const params = {
        id: this.availabilityId,
        therapist_name: name,
        params: `?date=${this.availabilityDate}&delete_option=${this.deleteOptionInput}`,
        message: null
      };

      this.conflictedAppointments = [];
      this.deleteLoading = true;
      const { success, appointments } = await this.deleteAvailabilities(params);
      if (!success) {
        this.deleteLoading = false;
        this.conflictedAppointments = appointments;
        return;
      }

      setTimeout(() => {
        this.deleteLoading = false;
        this.$emit("after-commit");
        this.$emit("close-modal");
      }, 1000);
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/availabilities/timeslot/EditAvailabilitiesModal.vue